// The 3D microphone: the model component (SuziModel / Suzi) and everything it needs - baked shading,
// split screen loading, material tuning constants, seams and creases, the no-fill pocket shading.
// Moved out of App.js unchanged; values tuned with ?lightdebug are applied at the bottom.
import * as THREE from 'three'
import React, { useRef, useLayoutEffect, useState, useMemo, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import { useGLTF, useTexture } from '@react-three/drei'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import TWEEN from '@tweenjs/tween.js'
import { useCerakoteMaterials, METAL_ENV } from './materials'
import { debugOverrides, DEV_TOOLS } from './debugParams'

// Virtual version of the product photo setup. Rendered once into the environment map,
// so it costs nothing per frame. Re-renders only when a setting changes (debug panel).
export const MIC_INTERIOR_PARTS = new Set([
  'body_magnets',
  'shield_plate',
  'capsule_mount',
  'capsule_mylar',
  'capsule_retainer_ring',
  'capsule_screens',
  'capsule_screens.001',
  'capsule_screens001',
  'ribbon',
  'ribbon_pcb',
  'ribbon_transducer',
  'ribbon_transducer_magnets',
  'ribbon_mount_screws',
  'ribbon_mounting_screws',
  'ribbon_nuts',
  'ribbon_screws'
])

export let MIC_INTERIOR_REFLECTION = 0.06

// When the screens are hidden to show the capsule or ribbon, the internals get full studio light
export const _revealRef = { current: 0 }

export const interiorLevel = (reveal) => MIC_INTERIOR_REFLECTION + (1 - MIC_INTERIOR_REFLECTION) * reveal

// NO LOGO FILL - the logo window looks onto the pocket floor of the body, 3 mm behind the logo plate. The body's
// baked shadows were made with the plate (and fill) in place, so that patch came out nearly black. With no fill,
// the body's baked shadows are eased inside that pocket only (box in model units; plate at z 0.026-0.0305, pocket
// floor at z 0.0228). Everywhere else the body is untouched.
export const POCKET = { min: [-0.06, 0.074, 0.02], max: [0.05, 0.203, 0.0262], ao: 0.9 }

export const _pocketUniforms = {
  uPocketOn: { value: 0 },
  uPocketMin: { value: new THREE.Vector3() },
  uPocketMax: { value: new THREE.Vector3() },
  uPocketAO: { value: POCKET.ao },
  // Mic Type reveal: the screens-off shadow bake was made with the frame ring in place, so the groove under the
  // ring came out pure black. While the screens (and ring) fade away, the body's baked shadows are floored at
  // this much light so the crevices read as deep, not artificially black. 0 when the screens are on.
  uAoFloor: { value: 0 }
}
export let REVEAL_AO_FLOOR = 0.5
if (DEV_TOOLS && typeof window !== 'undefined') window.__ohmaPocket = _pocketUniforms // for tuning with ?ohmadev

export function attachPocketAO(mat) {
  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, _pocketUniforms)
    shader.vertexShader = 'varying vec3 vPocketPos;\n' + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n  vPocketPos = position;')
    const inPocket = 'uPocketOn > 0.5 && all(greaterThan(vPocketPos, uPocketMin)) && all(lessThan(vPocketPos, uPocketMax))'
    shader.fragmentShader =
      'varying vec3 vPocketPos;\nuniform float uPocketOn;\nuniform vec3 uPocketMin;\nuniform vec3 uPocketMax;\nuniform float uPocketAO;\nuniform float uAoFloor;\n' +
      shader.fragmentShader.replace(
        '#include <aomap_fragment>',
        THREE.ShaderChunk.aomap_fragment
          .replace('* aoMapIntensity', `* ((${inPocket}) ? uPocketAO : aoMapIntensity)`)
          .replace(/(float ambientOcclusion = [^;]+;)/, '$1\n\tambientOcclusion = max( ambientOcclusion, uAoFloor );')
      )
  }
  mat.customProgramCacheKey = () => 'ohma-pocket-ao-2'
}

// CREVICE SHADOWS - screen-space ambient occlusion darkens the grille gaps, screen seams and
// logo cutout the way they read in the photos. Sizes are in scene units (the mic is about 0.6 tall).
export const AO_SETTINGS = { enabled: 0, radius: 0.025, falloff: 0.4, intensity: 6, halfRes: 1, smaa: 1, quality: 2, settleMs: 200, fadeMs: 200 }

// Logo fill is a 3D-printed plate: black with fine diagonal layer lines that catch the light.
// Pride flag: tint darkens the print (the fill used to be partly see-through, which darkened it; now
// that it is solid, the tint and lower env keep it as deep as the product photo instead of neon)
export const PRIDE_FILL = { env: 0.6, tint: 0.5 }

// Logo fill. Solid, never see-through (the model file's glass-like transmission is switched off here and
// on the back bottom fabric: it also made the renderer draw the whole mic twice every frame). White and
// black are matched to the product photos: white renders about #ece7e5 against #efe9e5 in the Teal/Apricot
// photos, black about #2d2d2d as on the Black/Stainless mic.
export const LOGO_FILL = { normal: 0.6, ao: 0.3, brightness: 0.6, blackBrightness: 0.17, lines: 90, roughness: 0.55, whiteEmissive: 0.52, whiteAo: 0.62, whiteEnv: 0.1 }

export const LOGO_FILL_WHITE_TINT = '#fffaf6'

export let _layerLineNormal = null

// Tileable normal map of 45 degree print layer lines, generated once (no extra download)
export function layerLineNormalMap() {
  if (_layerLineNormal) return _layerLineNormal
  const size = 256
  const data = new Uint8Array(size * size * 4)
  const k = 8 // lines per tile, an integer so the tile repeats seamlessly
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const t = ((x + y) / size) * k * Math.PI * 2
      const slope = Math.cos(t) * 0.7 // ridge profile derivative
      const nx = -slope / Math.SQRT2
      const ny = -slope / Math.SQRT2
      const nz = 1
      const len = Math.hypot(nx, ny, nz)
      const i = (y * size + x) * 4
      data[i] = ((nx / len) * 0.5 + 0.5) * 255
      data[i + 1] = ((ny / len) * 0.5 + 0.5) * 255
      data[i + 2] = ((nz / len) * 0.5 + 0.5) * 255
      data[i + 3] = 255
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat)
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping
  tex.repeat.set(LOGO_FILL.lines / k, LOGO_FILL.lines / k)
  tex.magFilter = THREE.LinearFilter
  tex.minFilter = THREE.LinearMipmapLinearFilter
  tex.generateMipmaps = true
  tex.needsUpdate = true
  _layerLineNormal = tex
  return tex
}

// SEAMS - the gap between each screen and the body is narrower than a pixel in the model, so it never
// reads as the dark line it is in the photos. Each screen gets a slightly larger black copy of itself,
// drawn just behind it: hidden by the screen and the body, it only shows through the gap.
// NORMALS - the CAD export has many sliver triangles and its normals on flat faces are bent 4 to 9 degrees,
// which shows up as streaks on the flat sides. Rebuild them: faces meeting at less than this angle are
// shaded smooth (fillets, bevels), sharper corners stay crisp, and flat faces come out truly flat.
export const CREASE = { enabled: 1, angle: 30 }

export const CREASE_PARTS = new Set(['body', 'Body1', 'Body1_1'])

// Area-weighted crease normals: tiny sliver triangles barely count, so flat faces stay flat
export function creasedNormalsWeighted(geometry, angleDeg) {
  const g = geometry.index ? geometry.toNonIndexed() : geometry.clone()
  const pos = g.attributes.position.array
  const faceCount = pos.length / 9
  const fn = new Float32Array(faceCount * 3) // area-weighted
  const fu = new Float32Array(faceCount * 3) // unit
  for (let f = 0; f < faceCount; f++) {
    const o = f * 9
    const ax = pos[o + 3] - pos[o], ay = pos[o + 4] - pos[o + 1], az = pos[o + 5] - pos[o + 2]
    const bx = pos[o + 6] - pos[o], by = pos[o + 7] - pos[o + 1], bz = pos[o + 8] - pos[o + 2]
    const nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx
    const len = Math.hypot(nx, ny, nz) || 1e-20
    fn[f * 3] = nx; fn[f * 3 + 1] = ny; fn[f * 3 + 2] = nz
    fu[f * 3] = nx / len; fu[f * 3 + 1] = ny / len; fu[f * 3 + 2] = nz / len
  }
  const q = 1e5
  const keyOf = (i) => Math.round(pos[i] * q) + ',' + Math.round(pos[i + 1] * q) + ',' + Math.round(pos[i + 2] * q)
  const byVertex = new Map()
  for (let f = 0; f < faceCount; f++) {
    for (let c = 0; c < 3; c++) {
      const k = keyOf(f * 9 + c * 3)
      let list = byVertex.get(k)
      if (!list) byVertex.set(k, (list = []))
      list.push(f)
    }
  }
  const cosA = Math.cos(THREE.MathUtils.degToRad(angleDeg))
  const normals = new Float32Array(pos.length)
  for (let f = 0; f < faceCount; f++) {
    const ux = fu[f * 3], uy = fu[f * 3 + 1], uz = fu[f * 3 + 2]
    for (let c = 0; c < 3; c++) {
      const list = byVertex.get(keyOf(f * 9 + c * 3))
      let sx = 0, sy = 0, sz = 0
      for (let i = 0; i < list.length; i++) {
        const h = list[i]
        if (ux * fu[h * 3] + uy * fu[h * 3 + 1] + uz * fu[h * 3 + 2] >= cosA) {
          sx += fn[h * 3]; sy += fn[h * 3 + 1]; sz += fn[h * 3 + 2]
        }
      }
      const l = Math.hypot(sx, sy, sz) || 1
      const o = f * 9 + c * 3
      normals[o] = sx / l; normals[o + 1] = sy / l; normals[o + 2] = sz / l
    }
  }
  g.setAttribute('normal', new THREE.BufferAttribute(normals, 3))
  return g
}

export const SEAM = { enabled: 0, grow: 0.012, depthPush: 6 }

// off: the model's screens sit with no clearance, so this halos the top grille
export const SEAM_SCREENS = /^(logo|custom_logo|custom_logo_back|(motif|stripes|scales|windows|holes|condenser|ribbon)_(top|bottomback)|Motif_to_Blender_v1003(_1)?|Logo_Screen_Cutout_For_Blender_122522(_1)?)$/

// Top fabric (saati) behind the screens. In the photos it reads close to black.
export let SAATI_TOP_OPACITY = 0.75

// BAKED SHADING - the model is served from the builder's own site with a second UV set, and each part has a
// precomputed ambient occlusion image (baked in Blender): accurate darkness in every crevice, seam and grille gap,
// at no runtime cost. Files live in /public/baked. The builder script is loaded into the Shopify page from
// customize.ohmaworld.com, so file URLs are resolved against the script's own origin, not the page's.
export const ASSET_ORIGIN = (() => {
  try {
    const src = document.currentScript && document.currentScript.src
    return src ? new URL(src).origin : window.location.origin
  } catch (e) {
    return ''
  }
})()

// geom: revision of the split model files. Files under /baked are cached for a year, so a changed file
// needs a new name (r2: same shapes, stored at a lower but still sub-pixel precision, about 20% smaller).
export const BAKED = { enabled: 1, version: 'v3', geom: 'r2', aoIntensity: 1.6, split: 1 }

// SPLIT LOADING - the body and everything else come in core.glb; each screen style is its own file.
// The build's own screen loads first (saved builds and share links included), the others follow in the
// background so switching stays instant. Cuts the first download from 5.7 MB to about 2.6 MB.
export const SCREEN_TYPES = ['motif', 'stripes', 'scales', 'windows', 'holes']

export const BAKED_MODEL_URL = `${ASSET_ORIGIN}/baked/${BAKED.version}/${BAKED.split ? `core-${BAKED.geom}` : 'ohma'}.glb`

export const SCREEN_URL = (type) => `${ASSET_ORIGIN}/baked/${BAKED.version}/screen_${type}-${BAKED.geom}.glb`

export const _screenState = new Map()

// type -> 'done' once its meshes are in the scene
export let _screenLoader = null

export function screenLoader() {
  if (!_screenLoader) {
    const draco = new DRACOLoader()
    draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.5/')
    _screenLoader = new GLTFLoader()
    _screenLoader.setDRACOLoader(draco)
  }
  return _screenLoader
}

// Downloads one screen style (cached). Safe to call early, before the body has finished loading.
export const _screenFetch = new Map()

export function fetchScreen(type) {
  if (!BAKED.enabled || !BAKED.split || !SCREEN_TYPES.includes(type)) return Promise.resolve(null)
  if (!_screenFetch.has(type)) {
    _screenFetch.set(
      type,
      new Promise((resolve) => screenLoader().load(SCREEN_URL(type), (gltf) => resolve(gltf), undefined, () => resolve(null)))
    )
  }
  return _screenFetch.get(type)
}

// Puts one screen style into the model. Resolves true when its meshes are in the scene.
export function loadScreen(scene, type) {
  if (_screenState.get(type) === 'done') return Promise.resolve(true)
  return fetchScreen(type).then((gltf) => {
    if (!gltf) return false
    if (_screenState.get(type) === 'done') return true
    const meshes = []
    gltf.scene.traverse((o) => o.isMesh && meshes.push(o))
    meshes.forEach((m) => {
      m.removeFromParent()
      m.visible = false // the material pass decides what shows
      scene.add(m)
    })
    _screenState.set(type, 'done')
    loadBakedAO(scene, [`${type}_top`, `${type}_bottomback`])
    return true
  })
}

export const BAKED_AO_URL = (part, small) => `${ASSET_ORIGIN}/baked/${BAKED.version}/ao_${part}${small ? '_1k' : ''}.webp`

// Parts that have a baked AO image (must match the bake list)
export const BAKED_PARTS = [
  'body', 'surround_top', 'surround_bottom',
  'motif_top', 'stripes_top', 'scales_top', 'windows_top', 'holes_top',
  'motif_bottomback', 'stripes_bottomback', 'scales_bottomback', 'windows_bottomback', 'holes_bottomback',
  'logo', 'custom_logo', 'custom_logo_back',
  'pop_filter_frame', 'pop_filter_ring_front', 'pop_filter_ring_back', 'pop_filter_spacer'
]

export const BAKED_PART_SET = new Set(BAKED_PARTS)

export const _bakedAO = new Map()

// part name -> loaded texture
// Shadows baked with the top screen and saati removed, swapped in while the screens are hidden for the
// mic-type reveal so the pocket and ledge don't keep the screen's shadow. Loaded after the main set.
export const BAKED_OPEN_PARTS = ['body', 'surround_top']

export const _bakedAOOpen = new Map()

export let _bakedAOStarted = false

export const SPLIT_SCREEN_PARTS = new Set(SCREEN_TYPES.flatMap((t) => [`${t}_top`, `${t}_bottomback`]))

export function loadBakedAO(scene, onlyParts) {
  if (!BAKED.enabled) return
  if (!onlyParts && _bakedAOStarted) return
  const small = typeof window !== 'undefined' && window.innerWidth < 1024
  const loader = new THREE.TextureLoader()
  // the first call loads the core parts; each screen's shadows load when that screen does
  const parts = onlyParts || BAKED_PARTS.filter((p) => !(BAKED.split && SPLIT_SCREEN_PARTS.has(p)))
  if (!onlyParts) _bakedAOStarted = true
  parts.forEach((part) => {
    if (_bakedAO.has(part)) return
    loader.load(BAKED_AO_URL(part, small), (tex) => {
      tex.flipY = false // glTF UV convention
      tex.channel = 1 // second UV set (TEXCOORD_1)
      tex.colorSpace = THREE.NoColorSpace
      tex.generateMipmaps = true
      tex.minFilter = THREE.LinearMipmapLinearFilter
      tex.anisotropy = 4
      _bakedAO.set(part, tex)
      const mesh = scene.getObjectByName(part)
      if (mesh && mesh.material && mesh._interiorCopy && mesh.material === mesh._interiorCopy) {
        mesh.material.aoMap = tex
        mesh.material.aoMapIntensity = BAKED.aoIntensity
        mesh.material.needsUpdate = true
      }
    })
  })
  if (onlyParts) return
  BAKED_OPEN_PARTS.forEach((part) => {
    loader.load(BAKED_AO_URL(`${part}_open`, small), (tex) => {
      tex.flipY = false
      tex.channel = 1
      tex.colorSpace = THREE.NoColorSpace
      tex.generateMipmaps = true
      tex.minFilter = THREE.LinearMipmapLinearFilter
      tex.anisotropy = 4
      _bakedAOOpen.set(part, tex)
    })
  })
}

export const MODEL_URL_ORIGINAL = 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260407_GLB%20UV.glb'

// Safety net: if the baked files are missing for any reason, fall back to the original model and the
// live crevice shadowing instead of breaking the builder. Suspends once while it checks.
export let _bakedOk = null

export let _bakedCheck = null

export function checkBaked() {
  if (!_bakedCheck) {
    _bakedCheck = (BAKED.enabled ? fetch(BAKED_MODEL_URL, { method: 'HEAD' }).then((r) => r.ok && !String(r.headers.get('content-type') || '').includes('text/html')) : Promise.resolve(false))
      .catch(() => false)
      .then((ok) => {
        _bakedOk = ok
        if (!ok) {
          BAKED.enabled = 0
          AO_SETTINGS.enabled = 1
        }
      })
  }
  return _bakedCheck
}

export const modelUrl = () => {
  if (_bakedOk === null) throw checkBaked()
  return _bakedOk ? BAKED_MODEL_URL : MODEL_URL_ORIGINAL
}

// Same studio, but with the white sweep lit like it is in the photos. Only brass and stainless use it.
// Built straight with PMREMGenerator (a second drei <Environment> interferes with the main one).
// Brushed screens use anisotropic reflections, which need a tangent (the direction of the brushing) at each
// vertex; without one three.js derives it per pixel from the UVs and it breaks along the CAD mesh's triangle
// edges. The brushing runs along the screen's u direction, so fit u across the whole part (least squares)
// and give every vertex that direction, laid flat against its normal: smooth, with no seams.
export function ensureTangents(obj) {
  const g = obj?.geometry
  if (!g || g.attributes.tangent || !g.attributes.normal || !g.attributes.uv) return
  const P = g.attributes.position
  const N = g.attributes.normal
  const U = g.attributes.uv
  const n = P.count
  if (n < 3) return
  // u = a.x*x + a.y*y + a.z*z + c, solved through the 4x4 normal equations
  const M = new Array(16).fill(0)
  const b = [0, 0, 0, 0]
  for (let i = 0; i < n; i++) {
    const r = [P.getX(i), P.getY(i), P.getZ(i), 1]
    const u = U.getX(i)
    for (let j = 0; j < 4; j++) {
      b[j] += r[j] * u
      for (let k = 0; k < 4; k++) M[j * 4 + k] += r[j] * r[k]
    }
  }
  const m4 = new THREE.Matrix4().fromArray(M).transpose()
  if (Math.abs(m4.determinant()) < 1e-12) return
  const sol = new THREE.Vector4(...b).applyMatrix4(m4.invert())
  const dir = new THREE.Vector3(sol.x, sol.y, sol.z)
  if (dir.lengthSq() < 1e-16) return
  dir.normalize()
  const t = new Float32Array(n * 4)
  const nv = new THREE.Vector3()
  const tv = new THREE.Vector3()
  for (let i = 0; i < n; i++) {
    nv.set(N.getX(i), N.getY(i), N.getZ(i)).normalize()
    tv.copy(dir).addScaledVector(nv, -dir.dot(nv))
    if (tv.lengthSq() < 1e-8) tv.set(1, 0, 0).addScaledVector(nv, -nv.x) // brushing straight into the face
    tv.normalize()
    t[i * 4] = tv.x
    t[i * 4 + 1] = tv.y
    t[i * 4 + 2] = tv.z
    t[i * 4 + 3] = 1
  }
  g.setAttribute('tangent', new THREE.BufferAttribute(t, 4))
}

export function SuziModel(
  {
    color,
    screenType: requestedScreenType,
    screenColor,
    fabricColor,
    bottomFabricColor,
    bottomScreenType,
    microphoneType,
    activeSelection,
    isThumbnail,
    isCapturingRef,
    isMobile,
    isMicButtonHovered = false,
    // accessory props (defaults to safe values)
    popFilterEnabled = false,
    popFilterColor,
    popFilterMaterial = 'plastic',
    shockMountEnabled = false,
    shockMountColor,
    fabricRotation = 0,
    texDebugValues = null,
    brassDebug = { repeatX: 0.3, repeatY: 0.1, rotation: 0 },
    stainlessDebug = { repeatX: 0.15, repeatY: 0.75, rotation: 3 },
    matDebugRef = null,
    matDebug = null,
    shadowsEnabled = false,
    ...props
  },
  ref
) {
  const { scene } = useGLTF(modelUrl())

  // Baked AO images load in the background and attach as they arrive
  useEffect(() => {
    loadBakedAO(scene)
  }, [scene])

  // Without split loading the model is complete as soon as this component has it
  useEffect(() => {
    if (!BAKED.enabled || !BAKED.split) requestAnimationFrame(() => window.dispatchEvent(new Event('ohma:model-ready')))
  }, [scene])

  // Screen styles: the one this build uses right away, then the rest one at a time in the background.
  // screensVersion re-runs the material pass whenever a screen arrives. Until a newly picked screen has
  // arrived, the previous one stays on so the mic never shows an empty top.
  const [screensVersion, setScreensVersion] = useState(0)
  const lastShownScreen = useRef(requestedScreenType)
  const screenReady = !BAKED.enabled || !BAKED.split || !SCREEN_TYPES.includes(requestedScreenType) || _screenState.get(requestedScreenType) === 'done'
  if (screenReady) lastShownScreen.current = requestedScreenType
  const screenType = screenReady ? requestedScreenType : lastShownScreen.current
  _pocketUniforms.uPocketOn.value = bottomFabricColor === 'none' ? 1 : 0 // see POCKET
  useEffect(() => {
    if (!BAKED.enabled || !BAKED.split) return
    let cancelled = false
    loadScreen(scene, requestedScreenType).then((ok) => {
      if (cancelled) return
      if (ok) setScreensVersion((v) => v + 1)
      // the mic is complete: let the loading screen go (next frame, once the screen has been drawn)
      requestAnimationFrame(() => window.dispatchEvent(new Event('ohma:model-ready')))
      const rest = SCREEN_TYPES.filter((t) => t !== requestedScreenType)
      const next = () => {
        const t = rest.shift()
        if (!t || cancelled) return
        loadScreen(scene, t).then((ok2) => {
          if (ok2 && !cancelled) setScreensVersion((v) => v + 1)
          // give the main thread room between files
          if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(next, { timeout: 1500 })
          else setTimeout(next, 300)
        })
      }
      setTimeout(next, 800)
    })
    return () => {
      cancelled = true
    }
  }, [scene, requestedScreenType])

  // Clean normals (see CREASE above), once per loaded model
  useMemo(() => {
    if (!CREASE.enabled || BAKED.enabled) return // the baked model already has clean welded normals
    scene.traverse((obj) => {
      if (!obj.isMesh || !obj.geometry || obj.geometry.userData.creased) return
      if (!CREASE_PARTS.has(obj.name)) return // only the body: that's where the streaks show, and it keeps load fast
      try {
        // Re-index afterwards so the mesh doesn't carry three copies of every vertex
        const g = mergeVertices(creasedNormalsWeighted(obj.geometry, CREASE.angle), 1e-6)
        g.userData.creased = true
        obj.geometry = g
      } catch (e) {
        // keep the original normals for this part
      }
    })
  }, [scene])

  // Seam lines (see SEAM above). Added once per screen mesh as a child, so it shows and hides with its screen.
  useMemo(() => {
    if (!SEAM.enabled) return
    const seamMaterial = new THREE.MeshBasicMaterial({
      color: '#000000',
      polygonOffset: true,
      polygonOffsetFactor: SEAM.depthPush,
      polygonOffsetUnits: SEAM.depthPush
    })
    const targets = []
    scene.traverse((obj) => {
      if (obj.isMesh && SEAM_SCREENS.test(obj.name) && !obj.children.some((c) => c.userData.isSeam)) targets.push(obj)
    })
    targets.forEach((screen) => {
      if (!screen.geometry.boundingBox) screen.geometry.computeBoundingBox()
      const center = screen.geometry.boundingBox.getCenter(new THREE.Vector3())
      const seam = new THREE.Mesh(screen.geometry, seamMaterial)
      const s = 1 + SEAM.grow
      seam.scale.setScalar(s)
      seam.position.copy(center).multiplyScalar(1 - s)
      seam.name = `${screen.name}__seam`
      seam.userData.isSeam = true
      seam.castShadow = false
      seam.receiveShadow = false
      seam.renderOrder = -1
      screen.add(seam)
    })
  }, [scene])
  const stainlessTexture = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/92f87f17-d522-4770-8e3a-517ad6982800/public')

  // Apply stainless debug overrides
  React.useEffect(() => {
    stainlessTexture.wrapS = stainlessTexture.wrapT = THREE.RepeatWrapping
    stainlessTexture.repeat.set(stainlessDebug.repeatX, stainlessDebug.repeatY)
    stainlessTexture.rotation = (stainlessDebug.rotation * Math.PI) / 2
    stainlessTexture.center.set(0.5, 0.5)
    stainlessTexture.needsUpdate = true
  }, [stainlessDebug, stainlessTexture])

  // Original brass texture for base color
  const brassTextureOriginal = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/845c1770-7d90-475a-f8e3-9364e97a6800/public')

  // Load brass PBR textures (use for detail maps only)
  const brassNormal = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/8d7ad806-b8c7-486a-8404-a3def07aa900/public')
  const brassAO = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/8e23a6c1-2bc7-428a-a4b0-14393eb32100/public')

  // Load all fabric PBR textures
  const fabricBaseColor = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/0134078c-e373-45e6-d761-776da9cb5700/public')
  const fabricOpacity = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/b5deea34-271d-4d8a-2a42-ee959664d100/public')
  const fabricRoughness = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/280d17c2-e063-4429-f816-1099bae5f200/public')
  const fabricNormal = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/f2ac5238-8535-4498-9fce-62389081a900/public')
  const fabricMetallic = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/3a9c8dda-48f5-4b52-7e9f-5392ed7d0900/public')
  const fabricAO = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/1e11882e-8538-485c-f6ed-a17af3dfae00/public')

  // Bottom fabric textures (STOSILENT for different look on bottom)
  const bottomFabricNormal = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/773581fc-638f-40de-9ca0-a2cd79706c00/public')
  const bottomFabricRoughness = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/144ccbd9-6ffb-41ad-1664-233a8ce98200/public')
  const bottomFabricAO = useTexture('https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/98d00700-99ac-4f97-3028-774baee88d00/public')

  // Pride logo fill texture — rainbow stripes, applied when bottomFabricColor === '#PRIDE'.
  // The fabric normal/roughness/AO maps still apply over this so it reads as woven fabric.
  const prideFabricTexture = useTexture('https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/pride-fabric-texture.webp')

  // Get material application functions from Materials.js
  const {
    applyCerakoteMaterial,
    applyScreenMaterial,
    applyBrassMaterial,
    applyBrassBodyMaterial,
    applyStainlessMaterial,
    applyAccessoryMaterial,
    setDebugRepeats
  } = useCerakoteMaterials(texDebugValues, matDebugRef, matDebug)

  // Configure original brass texture
  brassTextureOriginal.wrapS = brassTextureOriginal.wrapT = THREE.RepeatWrapping
  brassTextureOriginal.center.set(0.5, 0.5)

  // Brass body texture — smooth brass, same setup as screen
  const brassTextureBody = useTexture('https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/brass_smooth.webp')
  // Brass body roughness map — adds surface variation without affecting color
  const brassBodyNormalMap = useTexture('https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/brass_normal.webp')

  // Configure brass detail textures

  brassNormal.wrapS = brassNormal.wrapT = THREE.RepeatWrapping
  brassNormal.repeat.set(2, 2)
  brassNormal.needsUpdate = true

  brassAO.wrapS = brassAO.wrapT = THREE.RepeatWrapping
  brassAO.repeat.set(2, 2)
  brassAO.needsUpdate = true

  brassBodyNormalMap.wrapS = brassBodyNormalMap.wrapT = THREE.RepeatWrapping
  brassBodyNormalMap.encoding = THREE.LinearEncoding
  brassBodyNormalMap.repeat.set(1, 1)
  brassBodyNormalMap.needsUpdate = true

  // Apply brass debug overrides
  React.useEffect(() => {
    const rot = (brassDebug.rotation * Math.PI) / 2
    brassTextureOriginal.repeat.set(brassDebug.repeatX, brassDebug.repeatY)
    brassTextureOriginal.rotation = rot
    brassTextureOriginal.needsUpdate = true
    brassTextureBody.wrapS = brassTextureBody.wrapT = THREE.RepeatWrapping
    brassTextureBody.center.set(0.5, 0.5)
    brassTextureBody.repeat.set(0.01, 0.01)
    brassTextureBody.rotation = rot
    brassTextureBody.needsUpdate = true
  }, [brassDebug, brassTextureOriginal, brassTextureBody])

  // Create brass textures object for material function
  const brassTextures = {
    baseColor: brassTextureOriginal, // Screen uses original repeat (1,1)
    brushed: stainlessTexture, // brass screens: the stainless brushed texture and mapping, tinted brass
    normal: brassNormal,
    ao: brassAO
  }

  const brassTexturesBody = {
    baseColor: brassTextureBody,
    normal: brassBodyNormalMap,
    ao: brassAO
  }

  // Configure all fabric textures - smaller repeat = larger weave pattern
  fabricBaseColor.wrapS = fabricBaseColor.wrapT = THREE.RepeatWrapping
  fabricBaseColor.repeat.set(1, 1) // Much larger weave (was 3, 3)
  fabricBaseColor.rotation = Math.PI / 2 // Rotate 90 degrees

  fabricOpacity.wrapS = fabricOpacity.wrapT = THREE.RepeatWrapping
  fabricOpacity.repeat.set(1, 1)
  fabricOpacity.rotation = Math.PI / 2

  fabricRoughness.wrapS = fabricRoughness.wrapT = THREE.RepeatWrapping
  fabricRoughness.repeat.set(1, 1)
  fabricRoughness.rotation = Math.PI / 2

  fabricNormal.wrapS = fabricNormal.wrapT = THREE.RepeatWrapping
  fabricNormal.repeat.set(1, 1)
  fabricNormal.rotation = Math.PI / 2

  fabricMetallic.wrapS = fabricMetallic.wrapT = THREE.RepeatWrapping
  fabricMetallic.repeat.set(1, 1)
  fabricMetallic.rotation = Math.PI / 2

  fabricAO.wrapS = fabricAO.wrapT = THREE.RepeatWrapping
  fabricAO.repeat.set(1, 1)
  fabricAO.rotation = Math.PI / 2

  // Configure bottom fabric textures (STOSILENT)
  bottomFabricNormal.wrapS = bottomFabricNormal.wrapT = THREE.RepeatWrapping
  bottomFabricNormal.repeat.set(0.5, 0.5) // Reduced from 1 to make texture larger and more visible
  bottomFabricNormal.needsUpdate = true

  bottomFabricRoughness.wrapS = bottomFabricRoughness.wrapT = THREE.RepeatWrapping
  bottomFabricRoughness.repeat.set(0.5, 0.5)
  bottomFabricRoughness.needsUpdate = true

  bottomFabricAO.wrapS = bottomFabricAO.wrapT = THREE.RepeatWrapping
  bottomFabricAO.repeat.set(0.5, 0.5)
  bottomFabricAO.needsUpdate = true

  // Configure Pride fabric texture — clamp to edge, no rotation. We compress
  // the texture vertically with repeat.y > 1 so all 6 stripes fit into the
  // portion of UV space that the saati_bottom mesh actually samples. These
  // values were dialed in visually against the actual mesh UVs.
  prideFabricTexture.wrapS = prideFabricTexture.wrapT = THREE.ClampToEdgeWrapping
  prideFabricTexture.repeat.set(1, 3.5)
  prideFabricTexture.offset.set(0, -0.76)
  prideFabricTexture.rotation = 0
  prideFabricTexture.center.set(0.5, 0.5)
  prideFabricTexture.needsUpdate = true

  // Create separate "TOP" fabric textures so we can tone down saati_top without changing saati_bottom / saati_bottomback.
  // Goal: make the weave less intense by making the pattern smaller (more repeats) and lowering normal strength.
  const fabricBaseColorTop = useMemo(() => {
    const t = fabricBaseColor.clone()
    t.wrapS = t.wrapT = THREE.RepeatWrapping
    t.repeat.set(0.4, 0.4)
    t.center.set(0.5, 0.5)
    t.rotation = (fabricRotation * Math.PI) / 180 // Convert degrees to radians
    t.needsUpdate = true
    return t
  }, [fabricBaseColor, fabricRotation])

  const fabricOpacityTop = useMemo(() => {
    const t = fabricOpacity.clone()
    t.wrapS = t.wrapT = THREE.RepeatWrapping
    t.repeat.set(0.4, 0.4)
    t.center.set(0.5, 0.5)
    t.rotation = (fabricRotation * Math.PI) / 180
    t.needsUpdate = true
    return t
  }, [fabricOpacity, fabricRotation])

  const fabricRoughnessTop = useMemo(() => {
    const t = fabricRoughness.clone()
    t.wrapS = t.wrapT = THREE.RepeatWrapping
    t.repeat.set(0.4, 0.4)
    t.center.set(0.5, 0.5)
    t.rotation = (fabricRotation * Math.PI) / 180
    t.needsUpdate = true
    return t
  }, [fabricRoughness, fabricRotation])

  const fabricNormalTop = useMemo(() => {
    const t = fabricNormal.clone()
    t.wrapS = t.wrapT = THREE.RepeatWrapping
    t.repeat.set(0.4, 0.4)
    t.center.set(0.5, 0.5)
    t.rotation = (fabricRotation * Math.PI) / 180
    t.needsUpdate = true
    return t
  }, [fabricNormal, fabricRotation])

  const fabricMetallicTop = useMemo(() => {
    const t = fabricMetallic.clone()
    t.wrapS = t.wrapT = THREE.RepeatWrapping
    t.repeat.set(0.4, 0.4)
    t.center.set(0.5, 0.5)
    t.rotation = (fabricRotation * Math.PI) / 180
    t.needsUpdate = true
    return t
  }, [fabricMetallic, fabricRotation])

  const fabricAOTop = useMemo(() => {
    const t = fabricAO.clone()
    t.wrapS = t.wrapT = THREE.RepeatWrapping
    t.repeat.set(0.4, 0.4)
    t.center.set(0.5, 0.5)
    t.rotation = (fabricRotation * Math.PI) / 180
    t.needsUpdate = true
    return t
  }, [fabricAO, fabricRotation])

  // Pop filter fades in and out (about half a second) instead of popping on and off with the switch
  const popFade = useRef(popFilterEnabled ? 1 : 0)
  // The look the pop filter had while it was on. Picking None clears its color and material, so the
  // fade-out keeps this look instead of turning black on the way out.
  const popLook = useRef({ material: popFilterMaterial, color: popFilterColor })
  if (popFilterEnabled) popLook.current = { material: popFilterMaterial, color: popFilterColor }
  const POP_PARTS = ['pop_filter_frame', 'pop_filter_ring_back', 'pop_filter_ring_front', 'pop_filter_saati_back', 'pop_filter_saati_front', 'pop_filter_spacer']
  useLayoutEffect(() => {
    // // console.log('=== DEBUGGING NEW GLB ===')
    scene.traverse((obj) => {
      if (obj.isMesh) {
        // // console.log('Mesh found:', obj.name, 'Materials:', Array.isArray(obj.material) ? obj.material.map(m => m.name) : obj.material?.name)
      }
    })

    scene.traverse((obj) => {
      if (!obj.isMesh) return
      if (obj.userData.isSeam) return // seam lines follow their screen's visibility

      // Interior parts render with a private copy of their material (see end of this pass).
      // Put the shared material back first so everything below behaves exactly as before.
      if (obj._interiorCopy && obj.material === obj._interiorCopy) obj.material = obj._interiorSource

      // INTERIOR FACES - Apply very dark material for depth
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material]

      materials.forEach((mat, index) => {
        // BODY INTERIOR - Darkened body color
        if (mat && mat.name === 'ohma_body_inside') {
          const baseColor = new THREE.Color(color)
          baseColor.multiplyScalar(0.15) // 85% darker
          mat.color.copy(baseColor)
          mat.roughness = 0.9
          mat.metalness = 0.05
          mat.envMapIntensity = 0.05
        }

        // SCREEN INTERIOR - Darkened screen color
        if (mat && mat.name === 'ohma_screen_inside') {
          const baseColor = new THREE.Color(screenColor)
          baseColor.multiplyScalar(0.15) // 85% darker
          mat.color.copy(baseColor)
          mat.roughness = 0.9
          mat.metalness = 0.05
          mat.envMapIntensity = 0.05
        }
      })

      // Self-shadowing controlled by shadowsEnabled prop
      obj.receiveShadow = shadowsEnabled
      obj.castShadow = shadowsEnabled
      obj.visible = false

      // BODY - Apply Cerakote material system
      // Handle both old "body" name and new split meshes "Body1" and "Body1_1"
      // Also apply body color to surround_top (the frame ring around the screen opening)
      if (obj.name === 'body' || obj.name === 'Body1' || obj.name === 'Body1_1' || obj.name === 'surround_top') {
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material]
        // Log material names so we can see all slots

        materials.forEach((mat) => {
          // Skip interior materials - already set above
          if (mat && (mat.name === 'ohma_screen_inside' || mat.name === 'ohma_body_inside')) return

          if (color === 'Brass' || color === '#d5b173') {
            applyBrassBodyMaterial(mat, brassTexturesBody) // Use smooth body brass (no brushing)
          } else {
            applyCerakoteMaterial(mat, color)
          }
        })
        obj.visible = true
      }

      // BODY MAGNETS - Always visible, silver color
      if (obj.name === 'body_magnets') {
        obj.material.color.set('#C0C0C0') // Silver
        obj.material.roughness = 0.3
        obj.material.metalness = 0.95
        obj.visible = true
      }

      // CAPSULE RETAINER RING - Brass material
      if (obj.name === 'capsule_retainer_ring') {
        ensureTangents(obj)
        applyBrassMaterial(obj.material, brassTextures)
        obj.visible = true
      }

      // CAPSULE MYLAR - Shiny metal like stainless
      if (obj.name === 'capsule_mylar') {
        ensureTangents(obj)
        applyStainlessMaterial(obj.material, stainlessTexture)
        obj.visible = true
      }

      // CAPSULE MOUNT + TRANSDUCER - Force black when body is brass
      const isCapsuleOrRibbon = obj.name.includes('capsule') || obj.name.includes('ribbon')
      const isExplicitlyHandled = obj.name === 'capsule_retainer_ring' || obj.name === 'capsule_mylar'
      const isBodyMesh = obj.name === 'body' || obj.name === 'Body1' || obj.name === 'Body1_1' || obj.name === 'surround_top' || obj.name === 'body_magnets'
      if (isCapsuleOrRibbon && !isExplicitlyHandled && !isBodyMesh) {
        if (color === 'Brass' || color === '#d5b173') {
          // Store original material before cloning
          if (!obj._originalMaterial) obj._originalMaterial = obj.material
          if (!obj._blackMaterial) {
            obj._blackMaterial = obj._originalMaterial.clone()
            obj._blackMaterial.map = null
            obj._blackMaterial.color.set('#1c1d1d')
            obj._blackMaterial.roughness = 0.75
            obj._blackMaterial.metalness = 0.5
            obj._blackMaterial.envMapIntensity = 0.5
            obj._blackMaterial.needsUpdate = true
          }
          obj.material = obj._blackMaterial
        } else if (obj._originalMaterial) {
          // Restore original when not brass
          obj.material = obj._originalMaterial
        }
      }

      // ALL SCREWS AND MAGNETS - Make silver
      if (obj.name.includes('screw') || obj.name.includes('magnet') || obj.name.includes('nut') || obj.material?.name === 'magnets') {
        obj.material.color.set('#C0C0C0') // Silver
        obj.material.roughness = 0.3
        obj.material.metalness = 0.95
        obj.visible = true
      }

      // TOP FABRIC
      if (obj.name === 'saati_top') {
        const colorLower = String(fabricColor).trim().toLowerCase()
        const isBlack = colorLower === '#000000' || colorLower === '#000' || colorLower === 'black' || colorLower === '#1a1a1a'

        obj.material.map = fabricBaseColorTop
        obj.material.alphaMap = fabricOpacityTop
        obj.material.transparent = true
        obj.material.color.set(fabricColor) // Tint with white/black

        // No emissive for any color
        obj.material.emissive = new THREE.Color('#000000')
        obj.material.emissiveIntensity = 0

        obj.material.normalMap = fabricNormalTop
        obj.material.normalScale = new THREE.Vector2(0.6, 0.6) // Softer weave + less apparent lines
        obj.material.roughnessMap = fabricRoughnessTop
        obj.material.metalnessMap = fabricMetallicTop
        obj.material.aoMap = fabricAOTop
        obj.material.aoMapIntensity = 0.8 // Softer shadows on top weave (was 1.0)
        obj.material.alphaMap = fabricOpacity // For translucency
        obj.material.transparent = true
        obj.material.opacity = isThumbnail ? 1 : SAATI_TOP_OPACITY
        obj.material.roughness = 0.85 // Less reflective (was 0.8)
        obj.material.metalness = 0.0
        obj.material.envMapIntensity = 0.1 // Less reflective (was 0.2)
        obj.visible = true
      }

      // BOTTOM FABRIC FRONT
      if (obj.name === 'saati_bottom') {
        ensureClonedMaterial(obj)
        if (obj.material.transmission) obj.material.transmission = 0 // solid, never see-through
        // the logo fill can start as a copy of the screen material: keep the brushed-metal highlight off it
        if (obj.material && obj.material.anisotropy) obj.material.anisotropy = 0
        if (bottomFabricColor === 'none') {
          obj.visible = false
        } else if (bottomFabricColor === '#PRIDE') {
          // Pride logo fill — swap the woven base color texture for the rainbow
          // stripes image. We keep the normal map for subtle fabric weave depth
          // but remove the AO and metalness maps that were muddying the bright
          // colors. The roughness map stays for surface variation.
          obj.material.map = prideFabricTexture
          obj.material.color.setScalar(PRIDE_FILL.tint) // Deepens the print to match the photo
          obj.material.emissive = new THREE.Color('#000000')
          obj.material.emissiveIntensity = 0
          obj.material.normalMap = bottomFabricNormal
          obj.material.normalScale = new THREE.Vector2(0.3, 0.3) // Lighter weave so colors stay bright
          obj.material.roughnessMap = bottomFabricRoughness
          obj.material.metalnessMap = null // Removed — was making colors look metallic
          obj.material.aoMap = null // Removed — was darkening the colors
          obj.material.alphaMap = null // Removed — rainbow is fully opaque
          obj.material.transparent = false
          obj.material.roughness = 0.85
          obj.material.metalness = 0.0
          obj.material.envMapIntensity = PRIDE_FILL.env
          obj.material.needsUpdate = true
          obj.visible = true
        } else {
          // // console.log('saati_bottom fabric color:', bottomFabricColor) // DEBUG
          const colorLower = String(bottomFabricColor).trim().toLowerCase()
          const isWhite = colorLower === '#ffffff' || colorLower === '#fff' || colorLower === 'white' || colorLower === '#dcdcdc'
          const isBlack = colorLower === '#000000' || colorLower === '#000' || colorLower === 'black' || colorLower === '#1a1a1a'
          // // console.log('  isWhite:', isWhite, 'isBlack:', isBlack) // DEBUG

          obj.material.map = fabricBaseColor
          // Map circle color to mesh color (case insensitive)
          let meshColor = bottomFabricColor
          const colorLowerCheck = String(bottomFabricColor).trim().toLowerCase()
          if (colorLowerCheck === '#1a1a1a') {
            meshColor = '#404040' // Use lighter black for mesh when dark black is selected
          }

          // Use brighter white for white fabric with emissive
          if (isWhite) {
            // The white fill photographs as a slightly warm off-white (about #f0eae6)
            obj.material.color.set(LOGO_FILL_WHITE_TINT)
            obj.material.emissive = new THREE.Color(LOGO_FILL_WHITE_TINT)
            obj.material.emissiveIntensity = LOGO_FILL.whiteEmissive
          } else {
            obj.material.color.set(meshColor)
            obj.material.emissive = new THREE.Color('#000000')
            obj.material.emissiveIntensity = 0
          }

          // Logo fill: in the photos it reads as a smooth charcoal mesh with only a fine weave,
          // so the weave detail is kept subtle and the metallic flecks are off.
          obj.material.normalMap = layerLineNormalMap()
          obj.material.normalScale = new THREE.Vector2(LOGO_FILL.normal, LOGO_FILL.normal)
          obj.material.roughnessMap = null
          obj.material.metalnessMap = null
          obj.material.aoMap = bottomFabricAO
          obj.material.aoMapIntensity = isWhite ? LOGO_FILL.whiteAo : LOGO_FILL.ao
          if (!isWhite) {
            // Solid printed plastic: no fabric color map, a little sheen so the layer lines read
            obj.material.map = null
            // The black fill reads near-black in the photos (about 1/3 of the old level)
            obj.material.color.multiplyScalar(meshColor === '#404040' ? LOGO_FILL.blackBrightness : LOGO_FILL.brightness)
          }
          obj.material.alphaMap = fabricOpacity
          obj.material.transparent = false
          obj.material.roughness = isWhite ? 0.85 : LOGO_FILL.roughness
          obj.material.metalness = 0.0
          obj.material.envMapIntensity = isWhite ? LOGO_FILL.whiteEnv : 0.6
          obj.material.needsUpdate = true // Force material update
          obj.visible = true
        }
      }

      // BOTTOM FABRIC BACK
      if (obj.name === 'saati_bottomback') {
        ensureClonedMaterial(obj)
        if (obj.material.transmission) obj.material.transmission = 0 // solid, never see-through
        // the logo fill can start as a copy of the screen material: keep the brushed-metal highlight off it
        if (obj.material && obj.material.anisotropy) obj.material.anisotropy = 0
        // if front is “none”, use the top‐fabric color on the back
        // Back always mirrors top fabric -- logo fill only controls the front
        const backColor = fabricColor
        const colorLower = String(backColor).trim().toLowerCase()
        const isWhite = colorLower === '#ffffff' || colorLower === '#fff' || colorLower === 'white' || colorLower === '#dcdcdc'
        const isBlack = colorLower === '#000000' || colorLower === '#000' || colorLower === 'black' || colorLower === '#1a1a1a'

        obj.material.map = fabricBaseColor
        // Map circle color to mesh color (case insensitive)
        let meshColor = backColor
        const colorLowerCheck = String(backColor).trim().toLowerCase()
        if (colorLowerCheck === '#1a1a1a') {
          meshColor = '#404040' // Use lighter black for mesh when dark black is selected
        }
        obj.material.color.set(meshColor)

        // Add emissive for white to make it much brighter
        if (isWhite) {
          obj.material.emissive = new THREE.Color('#ffffff')
          obj.material.emissiveIntensity = 0.4
        } else {
          obj.material.emissive = new THREE.Color('#000000')
          obj.material.emissiveIntensity = 0
        }

        obj.material.normalMap = bottomFabricNormal
        obj.material.normalScale = new THREE.Vector2(0.6, 0.6) // Subtle fabric texture
        obj.material.roughnessMap = null
        obj.material.metalnessMap = null
        obj.material.aoMap = bottomFabricAO
        obj.material.aoMapIntensity = isWhite ? 0.8 : 1.8 // Stronger for visible weave
        obj.material.alphaMap = fabricOpacity
        obj.material.roughness = 0.98 // Extremely matte for bottomback
        obj.material.metalness = 0.0
        obj.material.envMapIntensity = 0.0 // No reflections at all for bottomback
        obj.material.transparent = false
        obj.material.needsUpdate = true // Force material update
        obj.visible = true
      }

      // SCREEN MATERIAL HELPER
      const updateScreenMaterial = () => {
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material]

        materials.forEach((mat) => {
          // Skip interior material - keep it dark
          if (mat && mat.name === 'ohma_screen_inside') return

          if (screenColor === 'Stainless Steel') {
            ensureTangents(obj)
            applyStainlessMaterial(mat, stainlessTexture)
          } else if (screenColor === 'Brass' || screenColor === '#d5b173') {
            ensureTangents(obj)
            applyBrassMaterial(mat, brassTextures, color === 'Brass' || color === '#d5b173', brassTexturesBody.baseColor)
          } else {
            // Use screen material system for colored screens
            applyScreenMaterial(mat, screenColor)
          }

          mat.needsUpdate = true
        })
        obj.visible = true
      }

      // ---------- ACCESSORIES runtime update (paste inside your scene.traverse callback) ----------
      const popFilterParts = [
        'pop_filter_frame',
        'pop_filter_ring_back',
        'pop_filter_ring_front',
        'pop_filter_saati_back',
        'pop_filter_saati_front',
        'pop_filter_spacer'
      ]
      const popFilterLogoName = 'pop_filter_logo'
      const shockMountParts = ['shockmount_body', 'shockmount_rubber', 'shockmount_thumbnut']

      // read current saati_top opacity so pop saati matches it live
      const saatiTop = scene.getObjectByName && scene.getObjectByName('saati_top')
      const saatiOpacityCurrent =
        saatiTop && saatiTop.material && typeof saatiTop.material.opacity === 'number' ? saatiTop.material.opacity : isThumbnail ? 1 : SAATI_TOP_OPACITY

      // Helper to ensure a material we can mutate
      function ensureClonedMaterial(mesh) {
        if (!mesh.material) return false
        if (!mesh.material._isCloned) {
          try {
            mesh.material = mesh.material.clone()
            mesh.material._isCloned = true
          } catch (e) {
            // console.warn('Failed to clone material for', mesh.name, e)
            return false
          }
        }
        return true
      }

      if (obj.name === popFilterLogoName) {
        // hide logo UI piece
        obj.visible = false
      }

      // POP FILTER pieces
      if (popFilterParts.includes(obj.name)) {
        // visible while enabled or still fading out; the per-frame fade sets the exact visibility
        obj.visible = !!popFilterEnabled || popFade.current > 0

        if (obj.material) {
          const ok = ensureClonedMaterial(obj)

          // decide color:
          // - saati pieces: always follow fabricColor (not affected by plastic/metal)
          // - other pieces: use popFilterColor
          let targetColor = color
          const isSaatiPiece = obj.name === 'pop_filter_saati_back' || obj.name === 'pop_filter_saati_front'

          if (isSaatiPiece) {
            targetColor = fabricColor
          } else {
            const popColor = popLook.current.color
            const hasUserPopColor = typeof popColor === 'string' && popColor.trim() !== ''
            targetColor = hasUserPopColor ? popColor : color
          }

          try {
            if (ok && targetColor) {
              if (isSaatiPiece) {
                // Apply fabric texture to pop filter saati (same as main saati with rotation)
                // // console.log('🎨 Applying fabric texture to', obj.name, 'color:', targetColor)
                const colorLower = String(targetColor).trim().toLowerCase()
                const isBlack = colorLower === '#000000' || colorLower === '#000' || colorLower === 'black' || colorLower === '#1a1a1a'
                // // console.log('  isBlack:', isBlack, 'fabricBaseColor loaded:', !!fabricBaseColor)

                obj.material.map = fabricBaseColorTop
                obj.material.color.set(targetColor)

                // No emissive
                obj.material.emissive = new THREE.Color('#000000')
                obj.material.emissiveIntensity = 0

                obj.material.normalMap = fabricNormalTop
                obj.material.normalScale = new THREE.Vector2(0.6, 0.6) // Softer weave + less apparent lines
                obj.material.roughnessMap = fabricRoughnessTop
                obj.material.metalnessMap = fabricMetallicTop
                obj.material.aoMap = fabricAOTop
                obj.material.aoMapIntensity = 1.5
                obj.material.alphaMap = fabricOpacityTop
                obj.material.transparent = false
                obj.material.opacity = 1.0
                obj.material.roughness = 0.85
                obj.material.metalness = 0.0
                obj.material.envMapIntensity = 0.1
                obj.material.needsUpdate = true
                // // console.log('  ✅ Fabric texture applied to', obj.name)
              } else {
                if (popLook.current.material === 'plastic') {
                  // Plastic pop filter — flat procedural black, no swatch texture
                  obj.material.map = null
                  obj.material.color.set('#232221')
                  obj.material.normalMap = null
                  obj.material.roughnessMap = null
                  obj.material.metalnessMap = null
                  obj.material.aoMap = null
                  obj.material.roughness = 0.9
                  obj.material.metalness = 0.0
                  obj.material.envMapIntensity = 0.1
                  obj.material.needsUpdate = true
                } else {
                  // Metal Cerakote pop filter — use swatch texture system
                  const isBrassBodyColor = color === 'Brass' || color === '#d5b173'
                  applyAccessoryMaterial(obj.material, targetColor, isBrassBodyColor)
                }
              }
            }
          } catch (e) {
            // console.warn('Unable to set color for', obj.name, e)
          }

          // For the saati pieces match transparency/opacity to saati_top
          if (isSaatiPiece) {
            obj.material.transparent = saatiOpacityCurrent < 1
            obj.material.opacity = saatiOpacityCurrent
          }

          obj.material.needsUpdate = true
        }
      }

      // SHOCK MOUNT pieces
      if (shockMountParts.includes(obj.name)) {
        obj.visible = !!shockMountEnabled

        if (obj.material) {
          const ok = ensureClonedMaterial(obj)

          // color rules:
          // - shockmount_rubber -> follow fabricColor (saati)
          // - shockmount_thumbnut -> always black
          // - shockmount_body -> user shockMountColor if set, else body color
          let targetColor = color
          if (obj.name === 'shockmount_rubber') {
            targetColor = fabricColor
          } else if (obj.name === 'shockmount_thumbnut') {
            targetColor = '#000000'
          } else {
            const hasUserShockColor = typeof shockMountColor === 'string' && shockMountColor.trim() !== ''
            targetColor = hasUserShockColor ? shockMountColor : color
          }

          try {
            if (ok && targetColor) {
              // Apply Cerakote accessory material for shock mount parts
              applyAccessoryMaterial(obj.material, targetColor)
            }
          } catch (e) {
            // console.warn('Unable to set shock mount color for', obj.name, e)
          }

          obj.material.needsUpdate = true
        }
      }
      // ---------- END accessories runtime update ----------

      // TOP SCREEN & BACK OF SCREEN
      // Handle both old names (motif_top, etc.) and new split meshes
      // NOTE: Logo meshes are bottom screen, not top screen
      const isScreenTopVisible = obj.name === `${screenType}_top` || obj.name === 'Motif_to_Blender_v1003' || obj.name === 'Motif_to_Blender_v1003_1'
      const allBottomBackTypes = ['condenser', 'ribbon', 'motif', 'stripes', 'scales', 'windows', 'holes']
      const isAnyBottomBack = allBottomBackTypes.some((t) => obj.name === `${t}_bottomback`)
      const isCurrentBottomBack = obj.name === `${screenType}_bottomback`

      if (isScreenTopVisible) {
        updateScreenMaterial()
      }

      // Explicitly show/hide all bottomback meshes — never leave stale visibility
      if (isAnyBottomBack) {
        updateScreenMaterial()
        if (isCurrentBottomBack && bottomScreenType === 'Custom Logo') {
          obj.visible = false // hide when custom logo replaces it
        } else {
          obj.visible = isCurrentBottomBack // show only matching type's back panel
        }
      }

      // BOTTOM SCREEN PATTERN
      const isBottomScreenVisible = bottomScreenType === 'Add Pattern Screen' && obj.name === `${screenType}_bottom`
      if (isBottomScreenVisible) {
        updateScreenMaterial()
        // Materials.js handles metalness - no override needed
      }

      // LOGO / CUSTOM LOGO — always explicitly set visible, never leave stale state
      if (obj.name === 'logo') {
        obj.visible = bottomScreenType === 'Logo'
        if (obj.visible) updateScreenMaterial()
      } else if (obj.name === 'custom_logo' || obj.name === 'custom_logo_back') {
        obj.visible = bottomScreenType === 'Custom Logo'
        if (obj.visible) updateScreenMaterial()
      }

      // MICROPHONE PARTS
      if ((microphoneType === 'Condenser' && obj.name.includes('capsule')) || (microphoneType === 'Ribbon' && obj.name.includes('ribbon'))) {
        obj.visible = true
      }

      // EXPLICIT TOP SCREEN VISIBILITY - Only show selected screen type
      const topScreenMeshes = {
        Motif_to_Blender_v1003: 'motif',
        Motif_to_Blender_v1003_1: 'motif'
      }

      if (topScreenMeshes[obj.name]) {
        obj.visible = topScreenMeshes[obj.name] === screenType
      }

      // EXPLICIT BOTTOM SCREEN VISIBILITY - Only show if Logo/Custom Logo selected
      const bottomScreenMeshes = ['Logo_Screen_Cutout_For_Blender_122522', 'Logo_Screen_Cutout_For_Blender_122522_1']

      if (bottomScreenMeshes.includes(obj.name)) {
        obj.visible = bottomScreenType === 'Logo' // Only show for Logo, not Custom Logo
      }

      // Dithering on every material hides 8-bit banding in smooth gradients on curved painted surfaces
      if (obj.material && !Array.isArray(obj.material) && !obj.material.dithering) {
        obj.material.dithering = true
        obj.material.needsUpdate = true
      }

      // INTERIOR PARTS - inside the real mic, the capsule, ribbon motor, screws and magnets sit in
      // shadow behind the screens, so they only catch a fraction of the studio light. Many of these
      // share a material with exterior parts, so they get a private copy with reflections scaled down.
      const isInterior = MIC_INTERIOR_PARTS.has(obj.name)
      const isBaked = BAKED.enabled && BAKED_PART_SET.has(obj.name)
      if ((isInterior || isBaked) && obj.material && !Array.isArray(obj.material)) {
        const src = obj.material
        const bakedAO = isBaked ? _bakedAO.get(obj.name) || null : null
        const programKey = [!!src.map, !!src.normalMap, !!src.roughnessMap, !!src.metalnessMap, !!(bakedAO || src.aoMap), !!src.alphaMap, src.transparent, src.dithering].join()
        if (!obj._interiorCopy || obj._interiorSource !== src || obj._interiorCopy.type !== src.type) {
          obj._interiorCopy = src.clone()
          obj._interiorSource = src
          obj._interiorCopy.needsUpdate = true
        } else {
          obj._interiorCopy.copy(src)
          if (obj._interiorKey !== programKey) obj._interiorCopy.needsUpdate = true
        }
        obj._interiorKey = programKey
        if (isInterior) {
          // Base level is kept so the mic-type reveal (screens hidden) can brighten it back up per frame
          obj._interiorCopy.userData.baseEnv = src.envMapIntensity ?? 1
          obj._interiorCopy.envMapIntensity = obj._interiorCopy.userData.baseEnv * interiorLevel(_revealRef.current)
        }
        if (bakedAO) {
          obj._interiorCopy.aoMap = bakedAO
          obj._interiorCopy.aoMapIntensity = BAKED.aoIntensity
        }
        if (obj.name === 'body') {
          // pocket box from model units into the body's own coordinates (once)
          if (!obj.userData.pocketBox) {
            scene.updateWorldMatrix(true, true)
            const toLocal = new THREE.Matrix4().copy(scene.matrixWorld).invert().multiply(obj.matrixWorld).invert()
            const box = new THREE.Box3()
            for (let i = 0; i < 8; i++) {
              box.expandByPoint(new THREE.Vector3(i & 1 ? POCKET.max[0] : POCKET.min[0], i & 2 ? POCKET.max[1] : POCKET.min[1], i & 4 ? POCKET.max[2] : POCKET.min[2]).applyMatrix4(toLocal))
            }
            obj.userData.pocketBox = box
          }
          _pocketUniforms.uPocketMin.value.copy(obj.userData.pocketBox.min)
          _pocketUniforms.uPocketMax.value.copy(obj.userData.pocketBox.max)
          attachPocketAO(obj._interiorCopy)
        }
        obj.material = obj._interiorCopy
      }
      if (obj.material && !Array.isArray(obj.material) && obj.material.userData.metalEnv) {
        obj.material.envMap = METAL_ENV.target.environment || null
      }
    })
  }, [
    screensVersion,
    color,
    screenType,
    screenColor,
    fabricColor,
    bottomFabricColor,
    bottomScreenType,
    microphoneType,
    isThumbnail,
    stainlessTexture,
    brassTextures,
    scene,
    popFilterEnabled,
    popFilterColor,
    popFilterMaterial,
    shockMountEnabled,
    shockMountColor,
    applyCerakoteMaterial,
    applyScreenMaterial,
    applyBrassMaterial,
    applyStainlessMaterial,
    applyAccessoryMaterial,
    matDebug,
    shadowsEnabled
  ])

  useEffect(() => {
    scene.traverse((obj) => {
      if (!obj.isMesh) return

      // Always keep the body visible (handle both old and new mesh names)
      if (obj.name === 'body' || obj.name === 'Body1' || obj.name === 'Body1_1') {
        obj.visible = true
      }

      // Only show the front bottom fabric if bottomFabricColor !== 'none'
      if (obj.name === 'saati_bottom') {
        obj.visible = bottomFabricColor !== 'none'
      }

      // Show only the correct mic‐part
      if ((microphoneType === 'Condenser' && obj.name.includes('capsule')) || (microphoneType === 'Ribbon' && obj.name.includes('ribbon'))) {
        obj.visible = true
      } else if (obj.name.includes('capsule') || obj.name.includes('ribbon')) {
        obj.visible = false
      }

      // Mic Type tab: the top screen, its fabric and the top surround are shown and hidden by the per-frame
      // fade below (useFrame), so they fade out and back in instead of blinking off here first
    })
  }, [
    activeSelection,
    microphoneType,
    screenType,
    screenColor,
    bottomFabricColor, // ← make sure this is in your deps
    isThumbnail,
    scene
  ])

  const lastSaatiVisible = useRef(null)
  const desktopMicFade = useRef(1) // 1 = fully visible, 0 = fully hidden

  useFrame(() => {
    TWEEN.update()

    if (scene) {
      const target = popFilterEnabled ? 1 : 0
      const was = popFade.current
      // pictures (saved builds, share image) and thumbnails always show the final state
      if (isThumbnail || isCapturingRef.current) popFade.current = target
      else popFade.current += (target - popFade.current) * 0.14
      if (Math.abs(popFade.current - target) < 0.01) popFade.current = target
      const f = popFade.current
      if (f !== was || (f > 0 && f < 1)) {
        POP_PARTS.forEach((name) => {
          const o = scene.getObjectByName(name)
          if (!o) return
          o.visible = f > 0
          const m = o.material
          // the frame, rings and spacer render with their baked-shadow copy, which is the one to fade
          if (!m || !(m._isCloned || m === o._interiorCopy)) return
          // the pop filter's fabric matches the top fabric's see-through level; everything else is solid
          const isSaati = name === 'pop_filter_saati_back' || name === 'pop_filter_saati_front'
          const st = isSaati ? scene.getObjectByName('saati_top') : null
          const base = st && st.material && typeof st.material.opacity === 'number' ? st.material.opacity : 1
          const fading = f < 1
          if (fading) {
            if (m._popWasTransparent === undefined) m._popWasTransparent = m.transparent
            if (!m.transparent) {
              m.transparent = true
              m.needsUpdate = true
            }
            m.opacity = base * f
          } else if (m._popWasTransparent !== undefined) {
            m.transparent = m._popWasTransparent
            m.opacity = base
            m.needsUpdate = true
            m._popWasTransparent = undefined
          }
        })
      }
    }

    if (scene && !isCapturingRef.current) {
      const saatiMesh = scene.getObjectByName('saati_top')
      const surroundTop = scene.getObjectByName('surround_top')

      // Get current screen meshes
      const currentScreenMeshes = [
        scene.getObjectByName(`${screenType}_top`),
        ...(screenType === 'motif' ? [scene.getObjectByName('Motif_to_Blender_v1003'), scene.getObjectByName('Motif_to_Blender_v1003_1')] : [])
      ].filter(Boolean)

      // Get ALL screen type meshes to hide non-current ones
      const allScreenTypes = ['condenser', 'ribbon', 'motif', 'stripes', 'scales', 'windows', 'holes']
      const allScreenMeshes = allScreenTypes
        .flatMap((type) => [
          scene.getObjectByName(`${type}_top`),
          ...(type === 'motif' ? [scene.getObjectByName('Motif_to_Blender_v1003'), scene.getObjectByName('Motif_to_Blender_v1003_1')] : [])
        ])
        .filter(Boolean)

      // FORCE visibility based on activeSelection — hide on mobile mic type tab,
      // OR on desktop when hovering the Ribbon/Condenser buttons
      const mobileHide = activeSelection === '6. Microphone Type' && isMobile
      const desktopHover = isMicButtonHovered && !isMobile

      // Mic Type tab on phones and the Ribbon/Condenser hover on desktop: the top screen, its fabric and
      // the top surround fade out (and back in) the same way on both
      const hideTop = mobileHide || desktopHover
      if (hideTop || desktopMicFade.current < 0.99) {
        const target = hideTop ? 0 : 1
        desktopMicFade.current += (target - desktopMicFade.current) * 0.08
        const fade = desktopMicFade.current
        // once fully faded, stop drawing them (and stop casting shadows); they come back as the fade returns
        const shown = !(hideTop && fade < 0.01)
        const saatiTarget = isThumbnail ? 1 : SAATI_TOP_OPACITY

        if (saatiMesh) {
          saatiMesh.visible = shown
          if (saatiMesh.material) {
            if (!saatiMesh.material.transparent) {
              saatiMesh.material.transparent = true
              saatiMesh.material.needsUpdate = true
            }
            saatiMesh.material.opacity = saatiTarget * fade
          }
        }
        // surroundTop: fade with saati
        if (surroundTop) {
          surroundTop.visible = shown
          if (surroundTop.material) {
            if (!surroundTop.material._clonedForFade) {
              surroundTop.material = surroundTop.material.clone()
              surroundTop.material._clonedForFade = true
            }
            if (!surroundTop.material.transparent) {
              surroundTop.material.transparent = true
              surroundTop.material.needsUpdate = true
            }
            surroundTop.material.opacity = fade
          }
        }
        // Current top screen mesh: fade with saati — clone material first so bottom meshes aren't affected
        allScreenMeshes.forEach((mesh) => {
          if (!mesh) return
          const shouldBeVisible = currentScreenMeshes.includes(mesh)
          mesh.visible = shouldBeVisible && shown
          if (shouldBeVisible && mesh.material) {
            if (!mesh.material._clonedForFade) {
              mesh.material = mesh.material.clone()
              mesh.material._clonedForFade = true
            }
            if (!mesh.material.transparent) {
              mesh.material.transparent = true
              mesh.material.needsUpdate = true
            }
            mesh.material.opacity = fade
          }
        })
      } else {
        // Fully visible — restore normal state
        desktopMicFade.current = 1
        const targetOpacity = isThumbnail ? 1 : SAATI_TOP_OPACITY
        if (saatiMesh) {
          if (saatiMesh.visible !== true) saatiMesh.visible = true
          if (saatiMesh.material) saatiMesh.material.opacity = targetOpacity
        }
        if (surroundTop) {
          if (surroundTop.visible !== true) surroundTop.visible = true
          if (surroundTop.material && surroundTop.material._clonedForFade) {
            surroundTop.material.transparent = false
            surroundTop.material.opacity = 1
            surroundTop.material.needsUpdate = true
            surroundTop.material._clonedForFade = false
          }
        }
        allScreenMeshes.forEach((mesh) => {
          if (!mesh) return
          const shouldBeVisible = currentScreenMeshes.includes(mesh)
          if (mesh.visible !== shouldBeVisible) mesh.visible = shouldBeVisible
          if (shouldBeVisible && mesh.material && mesh.material._clonedForFade) {
            mesh.material.transparent = false
            mesh.material.opacity = 1
            mesh.material.needsUpdate = true
            // dispose clone and let normal material logic reassign on next useEffect
            mesh.material._clonedForFade = false
          }
        })
      }

      // REVEAL - with the screens gone, the internals should read clearly: full reflections on the
      // capsule/ribbon, and the faded screen, saati and surround stop casting shadows into the body
      // (three.js draws transparent meshes into the shadow map as if they were solid).
      const reveal = Math.max(0, Math.min(1, 1 - desktopMicFade.current))
      if (Math.abs(reveal - _revealRef.current) > 0.002 || (reveal === 0 && _revealRef.current !== 0) || (reveal === 1 && _revealRef.current !== 1)) {
        _revealRef.current = reveal
        _pocketUniforms.uAoFloor.value = REVEAL_AO_FLOOR * reveal
        const level = interiorLevel(reveal)
        const casts = reveal < 0.5
        scene.traverse((obj) => {
          if (!obj.isMesh) return
          if (MIC_INTERIOR_PARTS.has(obj.name) && obj.material && obj.material.userData.baseEnv !== undefined) {
            obj.material.envMapIntensity = obj.material.userData.baseEnv * level
          }
        })
        ;[saatiMesh, surroundTop, ...allScreenMeshes].forEach((m) => {
          if (!m) return
          if (!casts) {
            if (!m.userData.shadowHidden) {
              m.userData.shadowHidden = true
              m.userData.castShadowBase = m.castShadow
            }
            m.castShadow = false
          } else if (m.userData.shadowHidden) {
            m.userData.shadowHidden = false
            m.castShadow = m.userData.castShadowBase
          }
        })
      }

      // The metal environment renders a frame after the materials are set up; hand it to brass/stainless
      const metalTex = METAL_ENV.target.environment
      if (metalTex && scene.userData.metalEnvTex !== metalTex) {
        scene.userData.metalEnvTex = metalTex
        scene.traverse((o) => {
          if (o.isMesh && o.material && !Array.isArray(o.material) && o.material.userData.metalEnv && o.material.envMap !== metalTex) {
            o.material.envMap = metalTex
            o.material.needsUpdate = true
          }
        })
      }

      // Swap the pocket/ledge shadows to the open-screen bake while the screens are mostly gone
      const wantOpen = reveal > 0.35
      BAKED_OPEN_PARTS.forEach((part) => {
        const m = scene.getObjectByName(part)
        const mat = m && m.material
        if (!mat || mat !== m._interiorCopy || !mat.aoMap) return
        const tex = wantOpen ? _bakedAOOpen.get(part) || _bakedAO.get(part) : _bakedAO.get(part)
        if (tex && mat.aoMap !== tex) mat.aoMap = tex
      })

      // Force only correct mic parts to be visible.
      // Exclude screen-type meshes (_top, _bottomback, _bottom) — e.g. ribbon_top
      // is a screen pattern mesh, not a mic internal, even though name contains 'ribbon'.
      const screenSuffixes = ['_top', '_bottomback', '_bottom']
      scene.traverse((obj) => {
        if (!obj.isMesh) return
        if (screenSuffixes.some((s) => obj.name.endsWith(s))) return
        const hasCapsule = obj.name.includes('capsule')
        const hasRibbon = obj.name.includes('ribbon')
        if (!hasCapsule && !hasRibbon) return
        if (microphoneType === 'Condenser' && hasRibbon && obj.visible !== false) {
          obj.visible = false
        } else if (microphoneType === 'Ribbon' && hasCapsule && obj.visible !== false) {
          obj.visible = false
        }
      })

      // Track saati_top visibility changes
      if (saatiMesh && saatiMesh.visible !== lastSaatiVisible.current) {
        lastSaatiVisible.current = saatiMesh.visible
      }
    }
  })

  return <primitive ref={ref} object={scene} {...props} />
}

// forwardRef so modelRef really points at the mic (it was always empty before, see micExtentsForView)
export const Suzi = React.forwardRef(SuziModel)

// ?lightdebug overrides for the values above
if (debugOverrides('ld').interiorReflection !== undefined) MIC_INTERIOR_REFLECTION = debugOverrides('ld').interiorReflection
if (debugOverrides('ld').revealAoFloor !== undefined) REVEAL_AO_FLOOR = debugOverrides('ld').revealAoFloor
if (debugOverrides('ld').saatiTopOpacity !== undefined) SAATI_TOP_OPACITY = debugOverrides('ld').saatiTopOpacity
for (const [k, v] of Object.entries(debugOverrides('ao'))) if (k in AO_SETTINGS) AO_SETTINGS[k] = v
for (const [k, v] of Object.entries(debugOverrides('lf'))) if (k in LOGO_FILL) LOGO_FILL[k] = v
if (debugOverrides('lf').prideEnv !== undefined) PRIDE_FILL.env = debugOverrides('lf').prideEnv
if (debugOverrides('lf').prideTint !== undefined) PRIDE_FILL.tint = debugOverrides('lf').prideTint
for (const [k, v] of Object.entries(debugOverrides('seam'))) if (k in SEAM) SEAM[k] = v
for (const [k, v] of Object.entries(debugOverrides('crease'))) if (k in CREASE) CREASE[k] = v
for (const [k, v] of Object.entries(debugOverrides('bake'))) if (k in BAKED) BAKED[k] = v
if (debugOverrides('pl').ao !== undefined) _pocketUniforms.uPocketAO.value = debugOverrides('pl').ao // &pl=ao:0.9

// ── UTM / referrer capture ────────────────────────────────────────────────────
// Read UTMs from the current URL and store in sessionStorage
// so they survive navigation into the configurator
;
